import React, { useEffect, useState } from 'react';
import { allCurrencies, CurrencyEnum, PlanDetailsModel } from '../../interfaces/plan.interface';
import PlanPurchaseItem from '../PlanPurchaseItem/PlanPurchaseItem';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { fetchPlansToSubscribe } from '../../store/thunks/app.thunk';
import { setTypePlansSubscription } from '../../store/appSlice';
import { createSubscriptionSessionAndRedirect } from '../../services/SelectPlanPurchase.service';
import { logOut } from '../../store/thunks/user.thunk';
import { getCurrencyForCountry } from '../../config/currencies';
import { debugPlanCurrency, logCurrencyDebug, validateCurrencyConfig } from '../../utils/currencyDebug';

interface SelectPlanPurchaseProps {
  closePlanPurchaseModal?: () => void;
  description?: string;
  closable?: boolean;
  showLogOut?: boolean;
}

const SelectPlanPurchase: React.FC<SelectPlanPurchaseProps> = ({
  closePlanPurchaseModal,
  showLogOut,
  description = '',
}: SelectPlanPurchaseProps) => {
  const [loader, setLoader] = useState<boolean>(true);
  const [submitButtonLoader, setSubmitButtonLoader] = useState<number>(-1);
  const [userCurrency, setUserCurrency] = useState<CurrencyEnum | null>(null);
  const user = useSelector((state: RootState) => state.user.user);
  const listPlansToSubscribe = useSelector((state: RootState) => state.app.listPlansToSubscribe);
  const typePlansSubscription = useSelector((state: RootState) => state.app.typePlansSubscription);
  const isFinishStory = useSelector((state: RootState) => state.app.isFinishStory);
  const listMonthPlansToSubscribe = useSelector((state: RootState) =>
    state.app.listPlansToSubscribe?.filter((plan) => plan.chargeCycle === 'month' || plan.chargeCycle === 'one_time'),
  );
  const listYearPlansToSubscribe = useSelector((state: RootState) =>
    state.app.listPlansToSubscribe?.filter((plan) => plan.chargeCycle === 'year' || plan.chargeCycle === 'one_time'),
  );
  const dispatch: AppDispatch = useDispatch();

  // Get user country and currency
  const getUserCountry = (): string | undefined => {
    return user?.shippingCountry || user?.detectedCountry;
  };

  const getUserCurrency = (): CurrencyEnum => {
    if (user?.detectedCurrency && allCurrencies.includes(user.detectedCurrency.toLowerCase())) {
      return user.detectedCurrency as CurrencyEnum;
    }
    const userCountry = getUserCountry();
    return userCountry ? getCurrencyForCountry(userCountry) || CurrencyEnum.USD : CurrencyEnum.USD;
  };

  // Initialize user currency on component mount
  useEffect(() => {
    const currency = getUserCurrency();
    setUserCurrency(currency);
  }, [user]);

  // Fetch plans when component mounts or user changes
  useEffect(() => {
    if (listPlansToSubscribe === null) {
      dispatch(fetchPlansToSubscribe());
    }
  }, [dispatch, listPlansToSubscribe]);

  useEffect(() => {
    setLoader(listPlansToSubscribe === null);
  }, [listPlansToSubscribe]);

  const changeTypeSubscription = (type: 'monthly' | 'yearly') => {
    dispatch(setTypePlansSubscription(type));
  };

  const sortPlans = (plan1: PlanDetailsModel, plan2: PlanDetailsModel) => {
    const cycleOrder = (plan1.chargeCycle === 'one_time' ? 0 : 1) - (plan2.chargeCycle === 'one_time' ? 0 : 1);
    return cycleOrder !== 0 ? cycleOrder : +plan1.cost - +plan2.cost;
  };

  const getPlansToSubscribe = (): PlanDetailsModel[] => {
    if (typePlansSubscription === 'monthly') {
      return listMonthPlansToSubscribe?.length ? listMonthPlansToSubscribe.sort(sortPlans) : [];
    } else {
      return listYearPlansToSubscribe?.length ? listYearPlansToSubscribe.sort(sortPlans) : [];
    }
  };

  const subscribeOnPlan = async (plan: PlanDetailsModel, index: number) => {
    if (!user) {
      return;
    }

    // Get user's currency for Stripe subscription
    const currency = userCurrency || getUserCurrency();
    const userCountry = getUserCountry();

    // Debug currency configuration
    const debugInfo = debugPlanCurrency(plan, userCountry, currency);
    logCurrencyDebug(debugInfo);

    // Validate currency configuration
    const errors = validateCurrencyConfig(debugInfo);
    if (errors.length > 0) {
      console.warn('Currency configuration issues:', errors);
    }

    console.log(`Creating subscription for plan ${plan.name} in currency ${currency}`);

    await createSubscriptionSessionAndRedirect(
      plan?.id,
      user?.email!,
      dispatch,
      true,
      index,
      isFinishStory,
      currency,
      () => {
        console.error('Failed to create subscription session');
      },
      setSubmitButtonLoader,
    );
  };

  const logOutUser = async () => {
    dispatch(
      logOut({
        callback: () => {
          setTimeout(() => closePlanPurchaseModal && closePlanPurchaseModal());
        },
      }),
    );
  };

  return (
    <div className="flex flex-col justify-center items-center">
      <div className="flex flex-col justify-center items-center">
        <span style={{ maxWidth: '816px', textAlign: 'center', fontSize: '17.5px' }}>{description}</span>
      </div>

      <div className="flex justify-center my-2 sm:my-4 lg:my-6 rounded-3xl bg-secondButtonBackground overflow-hidden shadow relative">
        <button
          className={`px-4 py-2 text-sm font-medium ${typePlansSubscription === 'monthly' ? 'bg-white' : 'bg-secondButtonBackground'} rounded-3xl`}
          onClick={() => changeTypeSubscription('monthly')}
        >
          Monthly
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${typePlansSubscription === 'yearly' ? 'bg-white' : 'bg-secondButtonBackground'} rounded-3xl`}
          onClick={() => changeTypeSubscription('yearly')}
        >
          Yearly
        </button>
      </div>
      {loader ? (
        <LoadingSpinner />
      ) : (
        <div className="relative w-full">
          <div
            className="flex justify-center items-center"
            style={{ position: 'absolute', width: '100%', height: '100%', zIndex: -1 }}
          >
            <img src="gradient_popup_bg@2x.svg" alt="" style={{ width: '75%', height: '75%' }} />
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 w-full justify-items-center px-4 py-2 sm:py-4 lg:py-6">
            {getPlansToSubscribe().map((plan, index) => (
              <PlanPurchaseItem
                key={plan.id}
                plan={plan}
                index={index}
                loader={submitButtonLoader === index}
                clickedOnPlan={(planData) => subscribeOnPlan(planData, index)}
                userCountry={getUserCountry()}
              />
            ))}
          </div>
        </div>
      )}
      {!!showLogOut && (
        <>
          <div className="mt-6 flex justify-center">
            <button className="text-sm text-teal-600 hover:text-teal-800" onClick={() => logOutUser()}>
              Log out
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectPlanPurchase;
